/**
 * Main PHQ-8 prediction display: a table of upcoming predictions (with an optional
 * simulated/counterfactual column) plus a combined historical+prediction+simulation
 * line chart. `predictions` is the real model output; `simulatedPredictions` is a second
 * call to the same endpoint with delta/regularity params from the "what-if" sliders
 * (see getPHQ8Prediction.ts on the backend and FeatureMiniChart.tsx for the per-feature
 * preview of the same transform).
 */
import { useState } from "react"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea } from "recharts"
import {
    AnalysisServiceGetPHQ8ModelMetadataResponsePayload,
    AnalysisServiceGetPHQ8PredictionResponsePayload
} from "pokal_api"

type PredictionScoreProps = {
    predictions: AnalysisServiceGetPHQ8PredictionResponsePayload | null
    simulatedPredictions?: AnalysisServiceGetPHQ8PredictionResponsePayload | null
    metadata: AnalysisServiceGetPHQ8ModelMetadataResponsePayload | null
    currentData?: Array<{ date: string; value: number }> | null
    selectedPatientfirstName: string | null
    selectedPatientlastName: string | null
}

function PredictionScore({ predictions, simulatedPredictions, metadata, currentData, selectedPatientfirstName, selectedPatientlastName }: PredictionScoreProps) {
    const [showOnlyToday, setShowOnlyToday] = useState(false)
    const todayString = new Date().toISOString().split("T")[0]

    const shouldDisplayPredictionForTargetDate = (targetDate: string) => {
        if (targetDate < todayString) return false
        if (showOnlyToday) return targetDate === todayString
        return true
    }

    type ChartRow = {
        x: number
        date: string
        historical?: number
        prediction?: number
        simulatedPrediction?: number
        sourceDate?: string
        simulatedSourceDate?: string
    }

    // Mirrors the PHQ-8 severity cut-offs from prediction/phq8_severity.py
    // (minimal/leicht/mittel/mittelschwer/schwer at 5/10/15/20) for the chip background.
    const getPredictionColor = (value: number) => {
        if (value <= 4) return "bg-green-100 text-green-900"
        if (value <= 9) return "bg-yellow-100 text-yellow-900"
        if (value <= 14) return "bg-orange-100 text-orange-900"
        if (value <= 24) return "bg-red-100 text-red-900"
        return "bg-red-200 text-red-950"
    }

    const getChartBandMax = (rows: ChartRow[]) => {
        const allValues = rows.flatMap((row) => [row.historical, row.prediction, row.simulatedPrediction])
        const numericValues = allValues.filter((value): value is number => typeof value === "number" && Number.isFinite(value))
        const maxValue = numericValues.length > 0 ? Math.max(...numericValues) : 24
        return Math.max(24, Math.ceil(maxValue))
    }

    // Merges three independent series onto one date-keyed timeline for the chart: the
    // patient's actual historical PHQ-8-relevant data, the real model predictions, and
    // (if simulating) the counterfactual predictions — each becomes its own <Line> below
    // so they render as separate colored series on shared X/Y axes.
    const prepareChartData = () => {
        const rows: ChartRow[] = []
        const historicalRows: ChartRow[] = []
        const currentDayBuckets = new Map<string, number[]>()
        const lookbackDays = metadata?.metadata.best_lookback ?? 0
        const horizonDays = metadata?.metadata.best_horizon ?? 0
        console.log("horizonDays:", horizonDays, "lookbackDays:", lookbackDays)
        let latestHistoricalTs = 0

        // Current patient values are aggregated to one daily mean and limited to the model lookback window.
        if (currentData && currentData.length > 0) {
            currentData.forEach((item) => {
                if (typeof item.value !== "number") return
                if (!currentDayBuckets.has(item.date)) {
                    currentDayBuckets.set(item.date, [])
                }
                currentDayBuckets.get(item.date)!.push(item.value)
            })

            currentDayBuckets.forEach((values, date) => {
                const dayTs = Date.parse(`${date}T00:00:00Z`)
                if (Number.isNaN(dayTs) || values.length === 0) return

                latestHistoricalTs = Math.max(latestHistoricalTs, dayTs)

                const sum = values.reduce((acc, value) => acc + value, 0)
                historicalRows.push({
                    x: dayTs,
                    date,
                    historical: sum / values.length
                })
            })

            if (lookbackDays > 0 && latestHistoricalTs > 0 && historicalRows.length > lookbackDays) {
                const cutoffTs = latestHistoricalTs - (lookbackDays + horizonDays - 1) * 24 * 60 * 60 * 1000
                console.log("Cutoff timestamp for historical data:", cutoffTs, "which corresponds to date:", new Date(cutoffTs).toISOString().split("T")[0])
                rows.push(...historicalRows.filter((row) => row.x >= cutoffTs))
            } else {
                rows.push(...historicalRows)
            }
        }

        // Keep every prediction entry and place it on its target date.
        if (predictions) {
            predictions.predictions.forEach((pred) => {
                if (typeof pred.prediction !== "number") return
            if (!shouldDisplayPredictionForTargetDate(pred.target_date)) return

                const dayTs = Date.parse(`${pred.target_date}T00:00:00Z`)
                if (Number.isNaN(dayTs)) return

                rows.push({
                    x: dayTs,
                    date: pred.target_date,
                    sourceDate: pred.source_date,
                    prediction: pred.prediction
                })
            })
        }

        if (simulatedPredictions) {
            simulatedPredictions.predictions.forEach((pred) => {
                if (typeof pred.prediction !== "number") return
            if (!shouldDisplayPredictionForTargetDate(pred.target_date)) return

                const dayTs = Date.parse(`${pred.target_date}T00:00:00Z`)
                if (Number.isNaN(dayTs)) return

                rows.push({
                    x: dayTs,
                    date: pred.target_date,
                    simulatedSourceDate: pred.source_date,
                    simulatedPrediction: pred.prediction
                })
            })
        }

        return rows.sort((a, b) => a.x - b.x)
    }

    const getFilteredPredictions = (source: AnalysisServiceGetPHQ8PredictionResponsePayload | null) => {
        if (!source || source.predictions.length === 0) return []

        const filtered = source.predictions.filter((pred) =>
            shouldDisplayPredictionForTargetDate(pred.target_date)
        )

        return filtered
    }

    if (!predictions) {
        return (
            <div className={"bg-gray-100 p-4 rounded-lg text-center text-gray-600"}>
                Keine Vorhersagen verfügbar
            </div>
        )
    }

    const filteredPredictions = getFilteredPredictions(predictions)
    const filteredSimulatedPredictions = getFilteredPredictions(simulatedPredictions ?? null)
    const hasValidPredictions = filteredPredictions.length > 0
    // Per-prediction: at least one day in that prediction's lookback window was missing
    // a feature value (flagged server-side by hasIncompleteWindow in getPHQ8Prediction.ts).
    const hasAnyIncompleteWindow = filteredPredictions.some((p) => p.has_incomplete_lookback)
    const horizon = metadata?.metadata.best_horizon ?? predictions?.horizon ?? 0
    // Whole-response: fewer future target dates were returned than the model's horizon
    // supports — i.e. the patient's data is stale, so only some of the possible future
    // days could be predicted at all.
    const hasPartialCoverage = filteredPredictions.length > 0 && horizon > 0 && filteredPredictions.length < horizon
    const newestSourceDate = filteredPredictions.length > 0
        ? filteredPredictions.reduce((a, b) => (a.source_date > b.source_date ? a : b)).source_date
        : null
    const newestSourceDateFormatted = newestSourceDate
        ? new Date(newestSourceDate + "T00:00:00Z").toLocaleDateString("de-DE", {
              day: "numeric", month: "long", year: "numeric", timeZone: "UTC"
          })
        : null
    const chartData = prepareChartData()
    const hasChartData = chartData.length > 0
    const chartBandMax = getChartBandMax(chartData)
    const simulatedPredictionByKey = new Map(
        filteredSimulatedPredictions.map((pred) => [
            `${pred.source_date}__${pred.target_date}`,
            pred
        ])
    )

    return (
        <div className={"flex flex-col space-y-4"}>

            {hasValidPredictions || hasChartData ? (
                <div className={"space-y-4"}>
                    <div className={"bg-white p-4 rounded-lg shadow space-y-6"}>
                        {hasValidPredictions ? (
                            <div className={"overflow-x-auto"}>
                            {newestSourceDateFormatted && (
                                <p className={"mb-3 text-sm text-gray-500"}>
                                    Basierend auf Daten bis <span className={"font-medium text-gray-700"}>{newestSourceDateFormatted}</span>
                                </p>
                            )}
                            {hasPartialCoverage && (
                                <div className={"mb-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800"}>
                                    <span>
                                        Die Datenbasis ist veraltet – Vorhersagen decken {filteredPredictions.length} von {horizon} möglichen Zukunftstagen ab. Für vollständige Abdeckung werden aktuelle Synchronisierungsdaten benötigt.
                                    </span>
                                </div>
                            )}
                            {hasAnyIncompleteWindow && (
                                <div className={"mb-4 flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800"}>
                                    <span>⚠</span>
                                    <span>
                                        Eingeschränkte Aussagekraft: Für mindestens eine Vorhersage ist das Lookback-Fenster unvollständig – es fehlen Datenpunkte für einzelne Features.
                                    </span>
                                </div>
                            )}
                            <div className={"flex items-center justify-between mb-4"}>
                                <h3 className={"text-lg font-semibold text-gray-900"}>Vorhersagen</h3>
                                <label className={"flex items-center space-x-2 text-sm"}>
                                    <input
                                        type="checkbox"
                                        checked={showOnlyToday}
                                        onChange={(e) => setShowOnlyToday(e.target.checked)}
                                        className={"w-4 h-4"}
                                    />
                                    <span className={"text-gray-700"}>Nur heutige Vorhersage anzeigen</span>
                                </label>
                            </div>
                            <div className={"flex items-center justify-between mb-4 text-sm text-gray-600"}>
                                Das Modell analysiert die jeweils letzten {metadata?.metadata.best_lookback} Tage an Oura-Daten (ausgehend vom Quelldatum) von {selectedPatientfirstName} {selectedPatientlastName} und sagt voraus, wie sich der PHQ-8-Score in den darauffolgenden {metadata?.metadata.best_horizon} Tagen entwickeln wird. Je aktueller die Datenbasis, desto weiter reicht der Vorhersagezeitraum in die Zukunft.
                            </div>
                            <table className={"w-full text-left"}>
                                <thead>
                                    <tr className={"border-b-2 border-gray-300"}>
                                        <th className={"px-4 py-2 text-gray-700"}>Quelldatum</th>
                                        <th className={"px-4 py-2 text-gray-700"}>Zieldatum</th>
                                        <th className={"px-4 py-2 text-gray-700"}>Vorhersage</th>
                                        <th className={"px-4 py-2 text-gray-700"}>Simulation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPredictions.map((pred, idx) => {
                                        const simulation = simulatedPredictionByKey.get(
                                            `${pred.source_date}__${pred.target_date}`
                                        )
                                        return (
                                        <tr key={idx} className={"border-b border-gray-200 hover:bg-gray-50"}>
                                            <td className={"px-4 py-2 text-gray-800"}>{pred.source_date}</td>
                                            <td className={"px-4 py-2 text-gray-800"}>{pred.target_date}</td>
                                            <td className={"px-4 py-2"}>
                                                <div className={"inline-flex items-center gap-1"}>
                                                    <span className={`px-3 py-1 rounded-full font-bold ${getPredictionColor(typeof pred.prediction === "number" ? pred.prediction : 0)}`}>
                                                        {typeof pred.prediction === "number" ? pred.prediction.toFixed(2) : pred.prediction}
                                                    </span>
                                                    {pred.has_incomplete_lookback && (
                                                        <span
                                                            className={"text-yellow-600 text-sm font-semibold"}
                                                            title={"Eingeschränkte Aussagekraft: Lookback-Fenster unvollständig"}
                                                        >
                                                            ▲!
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={"px-4 py-2"}>
                                                {simulation && typeof simulation.prediction === "number" ? (
                                                    <span className={`px-3 py-1 rounded-full font-bold ${getPredictionColor(simulation.prediction)}`}>
                                                        {simulation.prediction.toFixed(2)}
                                                    </span>
                                                ) : (
                                                    <span className={"text-gray-400"}>-</span>
                                                )}
                                            </td>
                                        </tr>
                                    )})}
                                </tbody>
                            </table>
                            </div>
                        ) : (
                            <div className={"rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600"}>
                                Die aktuellsten Daten liegen zu weit zurück – für heute oder die Zukunft sind keine Vorhersagen verfügbar.
                            </div>
                        )}

                        {hasChartData && (
                        <div className={"px-10 py-10 rounded-lg border"}>
                            <h3 className={"text-lg font-semibold text-gray-800 mb-4"}>Zeitlicher Verlauf</h3>
                            <p className={"text-sm text-gray-600 mb-6"}>
                                Diese Grafik zeigt den zeitlichen Verlauf von {selectedPatientfirstName} {selectedPatientlastName}{"'s"} PHQ-8-Score. Die blauen Punkte repräsentieren die historischen Daten, die grünen Punkte zeigen die Vorhersagen des Modells basierend auf den aktuellen Daten, und die roten Punkte zeigen die simulierten Vorhersagen basierend auf den eingestellten Deltas der Modell-Features.
                            </p>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={chartData}>
                                    <ReferenceArea y1={0} y2={4} fill="#dcfce7" fillOpacity={0.8} />
                                    <ReferenceArea y1={4} y2={9} fill="#fef9c3" fillOpacity={0.8} />
                                    <ReferenceArea y1={9} y2={14} fill="#ffedd5" fillOpacity={0.8} />
                                    <ReferenceArea y1={14} y2={24} fill="#fee2e2" fillOpacity={0.8} />
                                    <ReferenceArea y1={24} y2={chartBandMax} fill="#fecaca" fillOpacity={0.8} />
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="x"
                                        type="number"
                                        domain={["dataMin", "dataMax"]}
                                        tick={{ fontSize: 12 }}
                                        tickFormatter={(value) =>
                                            new Date(value as number)
                                                .toISOString()
                                                .split("T")[0]
                                        }
                                    />
                                    <YAxis
                                        domain={[0, chartBandMax]}
                                        label={{ value: "PHQ-8 Score", angle: -90, position: "insideLeft" }}
                                    />
                                    <Tooltip
                                        labelFormatter={(label) =>
                                            `Datum: ${new Date(label as number)
                                                .toISOString()
                                                .split("T")[0]}`
                                        }
                                        formatter={(value, name, item) => {
                                            if (typeof value !== "number") return value
                                            const payload = item.payload as ChartRow
                                            if (name === "Vorhersage" && payload.sourceDate) {
                                                return [
                                                    `${value.toFixed(2)} (Quelldatum: ${payload.sourceDate})`,
                                                    "Vorhersage"
                                                ]
                                            }
                                            if (name === "Simulation" && payload.simulatedSourceDate) {
                                                return [
                                                    `${value.toFixed(2)} (Quelldatum: ${payload.simulatedSourceDate})`,
                                                    "Simulation"
                                                ]
                                            }
                                            return [value.toFixed(2), name]
                                        }}
                                    />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="historical"
                                        stroke="#3b82f6"
                                        dot={{ fill: "#3b82f6", r: 4 }}
                                        activeDot={{ r: 6 }}
                                        name="Wert"
                                        connectNulls
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="prediction"
                                        stroke="#008720"
                                        dot={{ fill: "#008720", r: 4 }}
                                        activeDot={{ r: 6 }}
                                        name="Vorhersage"
                                        connectNulls
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="simulatedPrediction"
                                        stroke="#820002"
                                        dot={{ fill: "#820002", r: 4 }}
                                        activeDot={{ r: 6 }}
                                        name="Simulation"
                                        connectNulls
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className={"bg-yellow-50 border border-yellow-200 p-4 rounded-lg text-center text-yellow-800"}>
                    Aktuelle Daten sind nicht verfügbar, um genaue Vorhersagen für heute und später zu treffen.
                </div>
            )}
        </div>
    )
}

export default PredictionScore
